/**
 * Client for the backend's `/order-list` App Proxy endpoint (see
 * backend/src/order_list_service/views.py). Identity/token handling lives in
 * the shared js/objects/proxyAuth.js — the backend verifies the HMAC itself
 * and derives the company from the signed customer id, so callers here never
 * send a company id.
 */
import API_ENDPOINTS from '../../config/api.js';
import { proxyFetch } from '../proxyAuth.js';

// Keep in sync with ORDER_LIST_COUNT in backend/src/order_list_service/helpers.py.
export const ORDER_LIST_COUNT = 30;

/**
 * Upsert one order-list slot (1-30) on the customer's company.
 * `data` is any JSON-serialisable value; the backend stores it verbatim.
 */
export async function putOrderList(index, data) {
  const response = await proxyFetch(API_ENDPOINTS.ORDER_LIST_PROXY, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ index, data }),
  });

  if (!response.ok) {
    throw new Error(`order-list PUT HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Read every list slot straight from the backend (Admin API), as list objects
 * with `metafieldKey` (the slot) and a stable `id` attached, in slot order.
 *
 * COB2B-785: anything that's about to write a list must read through here, not
 * from Liquid (company.metafields rendered into the page / ?view=lists-json).
 * The storefront lags behind Admin API writes, and since every PUT replaces a
 * whole slot, building a write on that stale copy wiped items that had just
 * been saved — and picking a "free" slot from it overwrote a list that had
 * just been created.
 */
export async function getOrderLists() {
  const response = await proxyFetch(API_ENDPOINTS.ORDER_LIST_PROXY, {
    method: 'GET',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`order-list GET HTTP ${response.status}`);
  }
  const { lists } = await response.json();

  const result = [];
  for (let i = 1; i <= ORDER_LIST_COUNT; i += 1) {
    const key = metafieldKeyFromIndex(i);
    const value = lists && lists[key];
    // A deleted list is PUT as null; anything that isn't a list object is an empty slot.
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result.push({ ...value, metafieldKey: key, id: value.id || key });
    }
  }
  return result;
}

/** Extract the 1-30 slot index from a metafield key like "order_list_5". */
export function indexFromMetafieldKey(metafieldKey) {
  const match = /^order_list_(\d+)$/.exec(metafieldKey || '');
  return match ? Number(match[1]) : null;
}

/** Build the metafield key for a slot index, e.g. 5 -> "order_list_5". */
export function metafieldKeyFromIndex(index) {
  return `order_list_${index}`;
}

/**
 * Find the lowest 1-30 slot not already used by `lists` (each with a
 * `metafieldKey`). Returns null if all 30 slots are taken.
 */
export function findFreeSlotIndex(lists) {
  const used = new Set(
    (lists || [])
      .map((l) => indexFromMetafieldKey(l && l.metafieldKey))
      .filter(Boolean),
  );
  for (let i = 1; i <= ORDER_LIST_COUNT; i += 1) {
    if (!used.has(i)) return i;
  }
  return null;
}

/** Timestamp a list was last touched, for ordering. Missing/invalid sorts last. */
function lastUpdatedTime(list) {
  const t = new Date(list.updatedAt || list.createdAt).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Copy of `lists`, most recently updated first — the order the Your Lists
 * page and the "Save to List" drawer both show (COB2B-785).
 */
export function sortListsByLastUpdated(lists) {
  return [...(lists || [])].sort(
    (a, b) => lastUpdatedTime(b) - lastUpdatedTime(a),
  );
}

/**
 * Id for a new list, matching what the Your Lists page generates.
 * Falls back to a timestamp where crypto.randomUUID is unavailable.
 * Shared by saveCartToList.js and addToListLogin.js's inline "create a list".
 */
export function generateListId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `list-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}
